import React from "react";
import PropTypes from "prop-types";
import throttle from "lodash/throttle";
import { connect } from "react-redux";
import styled, { ThemeProvider } from "styled-components";
import queryString from "querystring";
import uuid from "uuid";
import {
  setHandleNcsItem,
  setHandleNcsAppInfo,
  setHandlePlaceholder,
  initializeContainer,
  requestAppInfo,
  getNewsroomOrigin
} from "../api/container";
import { initApiListener } from "../api/remoteControl";
import App from "../constants/app";
import { actions as assetActions } from "../actions/asset";
import { actions as assetsActions } from "../actions/assets";
import { actions as authActions } from "../actions/auth";
import { actions as appActions } from "../actions/app";
import { actions as keyShortcutsActions } from "../actions/keyShortcuts";
import { actions as snackbarActions } from "../actions/snackbar";
import FullScreenOverlay from "../components/FullScreenOverlay";
import AppMode from "../constants/appMode";
import { parseAppInfoMessage } from "../util/mosUtils";
import localize from "../util/localizeUtils";
import LoadingIndicator from "../components/LoadingIndicator";
import SnackBar from "../components/SnackBar";
import ViewPortSize from "../constants/viewPortSize";
import RunMode from "../constants/appRunMode";
import ContainerType from "../constants/containerType";
import GlobalStyle from "../styled-components/GlobalStyle";
import SavedStateHandler from "../components/SavedStateHandler";

if (process.env.NODE_ENV === "development") {
  document.domain = window.location.hostname;
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  -webkit-transition: all 0.5s ease;
  -moz-transition: all 0.5s ease;
  -ms-transition: all 0.5s ease;
  -o-transition: all 0.5s ease;
  transition: all 0.5s ease;

  height: 100%;

  &.LEFT_ONLY {
    margin-left: ${props => props.sidebarWidth};
    margin-right: 0;
  }
  &.NONE {
    margin-left: 0;
    margin-right: 0;
  }
  &.RIGHT_ONLY {
    margin-left: 0;
    margin-right: ${props => props.sidebarWidth};
  }
  &.BOTH {
    margin-left: ${props => props.sidebarWidth};
    margin-right: ${props => props.sidebarWidth};
  }
`;

const OverlayText = styled.div`
  font-size: 6rem;
  text-align: center;
  line-height: 6rem;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const isKeyHeld = (event, key) => {
  if (event && key && event.keyCode === key) {
    return event.type === "keydown";
  }
  return false;
};

class CoreLayout extends React.Component {
  constructor() {
    super();

    this.state = {
      receivedLogin: false
    };

    this.checkLoginTimeout = null;
    this.queuedLoginAttempt = null;
    if (this.getContainerType() !== ContainerType.NONE) {
      initializeContainer();
    }
    initApiListener();
  }

  UNSAFE_componentWillMount() {
    const containerType = this.getContainerType();
    const user = this.getUserFromQs();

    window.addEventListener("resize", this.onResize);
    window.addEventListener("keydown", this.handleKeyPress);
    window.addEventListener("keyup", this.handleKeyPress);

    this.props.setContainerType(containerType);
    this.props.setViewPortSize(this.getViewPort());

    // If it's the clipper or in a newsroom system, don't automatically try to log in.
    if (
      !this.context.router.isActive("/clipper/") &&
      containerType !== ContainerType.NONE
    ) {
      setHandleNcsItem(this.handleNcsItem);
      setHandleNcsAppInfo(this.handleNcsAppInfo);
      setHandlePlaceholder(this.handlePlaceholder);
      // If the query string param is set, override the user.
      if (user) {
        console.log(`Found querystring parameter 'UserName=?${user}`);
        this.props.login(user, false, true);
      } else {
        this.props.login(null, false, false, true);
        console.log("Waiting for newsroom to send user.");
        // Wait 5 seconds before checking login again and spawning the login dialog.
        this.checkLoginTimeout = setTimeout(() => this.checkLogin, 5000);
      }
    } else {
      if (user) {
        console.log(`Found querystring parameter 'UserName=?${user}`);
      }
      // LUCI is being run in a browser.
      this.props.login(user, false, true);
    }
  }

  componentDidMount() {
    // Send request for app info immediately, and then shortly later if no response was sent back.
    if (this.getContainerType() !== ContainerType.NONE) {
      this.appInfoTimeout = setTimeout(() => requestAppInfo(), 1500);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.isLoggingIn &&
      !nextProps.isLoggingIn &&
      this.queuedLoginAttempt
    ) {
      console.log("Executing queued login attempt");
      this.props.login(this.queuedLoginAttempt);
      this.queuedLoginAttempt = null;
    }

    if (
      !nextProps.initialized &&
      !nextProps.initializing &&
      !this.props.authenticated &&
      nextProps.authenticated
    ) {
      this.props.initialize();
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (this.props.initialized) {
      if (
        this.props.currentUser !== nextProps.currentUser ||
        (nextProps.authenticated &&
          !this.props.configLoaded &&
          nextProps.configLoaded)
      ) {
        this.props.loadUserConfig();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyPress);
    window.removeEventListener("keyup", this.handleKeyPress);
    window.removeEventListener("resize", this.onResize);
  }

  onResize = throttle(() => {
    this.props.setViewPortSize(this.getViewPort());
  }, 500);

  getUserFromQs = () => {
    const qs = queryString.parse(document.location.search);
    return qs.UserName || this.props.location.query.UserName;
  };

  getContainerType = () => {
    const newsRoomOrigin = getNewsroomOrigin();
    const isActiveX = !!window.chymox;

    if (newsRoomOrigin) {
      return ContainerType.HTML;
    } else if (isActiveX) {
      return ContainerType.ACTIVEX;
    }
    return ContainerType.NONE;
  };

  getViewPort = () => {
    const width = window.innerWidth;
    let viewPort = ViewPortSize.LARGE;
    if (width < ViewPortSize.MEDIUM) {
      viewPort = ViewPortSize.SMALL;
    } else if (width < ViewPortSize.LARGE) {
      viewPort = ViewPortSize.MEDIUM;
    }
    return viewPort;
  };

  getSidebarWidth = () => {
    const { viewPortSize } = this.props;
    if (viewPortSize < ViewPortSize.LARGE) {
      return App.SIDEBAR_MIN_WIDTH;
    }
    return App.SIDEBAR_WIDTH;
  };

  // Used to check that we received a login from the NCS appInfo in a reasonable amount of time.
  checkLogin = () => {
    console.log("Verifying that LUCI has received a login from the NCS.");
    this.props.login();
  };

  handleKeyPress = event => {
    const isModifierActive = isKeyHeld(event, 18);
    if (
      this.props.isModifierActive !== isModifierActive &&
      event.keyCode === 18
    ) {
      this.props.setModifierKeyState(isModifierActive);
    }
  };

  handleNcsItem = message => {
    this.props.openMosObject(message);
  };

  handleNcsAppInfo = message => {
    clearTimeout(this.appInfoTimeout);

    const ncsAppInfo = parseAppInfoMessage(message);
    const qsUser = this.getUserFromQs();

    if (ncsAppInfo) {
      this.props.setAppRunMode(RunMode.NCS);
    }

    if (
      !qsUser &&
      ncsAppInfo.ncsInformation.userID &&
      ncsAppInfo.ncsInformation.userID.toLowerCase() !==
        this.props.currentUser.toLowerCase() &&
      !this.props.authenticated
    ) {
      console.log(
        `Received login user from NCS.  Logging in as '${ncsAppInfo.ncsInformation.userID}'`
      );
      if (this.props.isLoggingIn) {
        this.queuedLoginAttempt = ncsAppInfo.ncsInformation.userID;
      } else {
        this.props.login(ncsAppInfo.ncsInformation.userID);
        clearTimeout(this.checkLoginTimeout);
      }
    }

    if (ncsAppInfo.ncsInformation) {
      this.props.setNcsInfo(ncsAppInfo.ncsInformation);
    }

    if (ncsAppInfo.ncsInformation.context) {
      this.props.setDefaultContext(ncsAppInfo.ncsInformation.context);
    }

    if (ncsAppInfo.ncsId && ncsAppInfo.ncsId !== this.props.ncsId) {
      console.log(`Setting NCS ID to ${ncsAppInfo.ncsId}`);
      this.props.setNcsId(ncsAppInfo.ncsId);
    }

    if (
      ncsAppInfo.ncsInformation.runContext &&
      ncsAppInfo.ncsInformation.runContext !== this.props.runContext
    ) {
      console.log(
        `Setting run context to ${ncsAppInfo.ncsInformation.runContext}`
      );
      this.props.setRunContext(ncsAppInfo.ncsInformation.runContext);
    }

    if (ncsAppInfo.mosObj && ncsAppInfo.mosObj.mosObjectId !== "XXX") {
      console.log(
        "Detected mosObj or item in ncsAppInfo message.  Loading mos object."
      );
      console.log(ncsAppInfo.mosObj);
      this.props.openMosObject(message);
    }
  };

  handlePlaceholder = message => {
    if (message.mos.item) {
      this.props.openPlaceholder(message.mos.item[0].itemID[0]);
    } else if (message.mos.ncsAppInfo && message.mos.ncsAppInfo[0].item) {
      this.props.openPlaceholder(message.mos.ncsAppInfo[0].item[0].itemID[0]);
    } else {
      this.props.openPlaceholder(message.mos.itemID[0]);
    }
  };

  render() {
    const { children, appMode, assetLoading, initialized, theme } = this.props;

    return (
      <ThemeProvider theme={theme}>
        <Container>
          <SavedStateHandler />
          <GlobalStyle />
          <FullScreenOverlay visible={appMode === AppMode.CLIPPER_ACTIVE}>
            <OverlayText>{localize("exitClipperText")}</OverlayText>
          </FullScreenOverlay>
          <LoadingIndicator
            visible={
              assetLoading ||
              (!initialized && !this.context.router.isActive("/login/"))
            }
          />
          <Container>{children}</Container>
          <SnackBar />
        </Container>
      </ThemeProvider>
    );
  }
}

CoreLayout.defaultProps = {
  params: {
    userName: ""
  },
  ncsId: null,
  insertedObjId: null
};

CoreLayout.contextTypes = {
  router: PropTypes.object
};

CoreLayout.propTypes = {
  children: PropTypes.element.isRequired,
  sidebar: PropTypes.string.isRequired,
  openMosObject: PropTypes.func.isRequired,
  openPlaceholder: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  location: PropTypes.shape({
    query: PropTypes.shape({
      UserName: PropTypes.string
    }).isRequired
  }).isRequired,
  loadChannelList: PropTypes.func.isRequired,
  currentUser: PropTypes.string.isRequired,
  ncsId: PropTypes.string,
  setNcsId: PropTypes.func.isRequired,
  authenticated: PropTypes.bool.isRequired,
  appMode: PropTypes.string.isRequired,
  loadConfig: PropTypes.func.isRequired,
  loadUserConfig: PropTypes.func.isRequired,
  configLoaded: PropTypes.bool.isRequired,
  runContext: PropTypes.string.isRequired,
  setRunContext: PropTypes.func.isRequired,
  assetLoading: PropTypes.bool.isRequired,
  setModifierKeyState: PropTypes.func.isRequired,
  isModifierActive: PropTypes.bool.isRequired,
  inserting: PropTypes.bool.isRequired,
  insertFailed: PropTypes.bool.isRequired,
  insertedObjId: PropTypes.string,
  showSnack: PropTypes.func.isRequired,
  setViewPortSize: PropTypes.func.isRequired,
  viewPortSize: PropTypes.number.isRequired,
  setAppRunMode: PropTypes.func.isRequired,
  setContainerType: PropTypes.func.isRequired,
  isLoggingIn: PropTypes.bool.isRequired,
  initializing: PropTypes.bool.isRequired,
  initialized: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
  checkConnectionStatus: PropTypes.func.isRequired,
  setDefaultContext: PropTypes.func.isRequired,
  setNcsInfo: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  sidebar: state.app.sidebar,
  authenticated: state.auth.authenticated,
  currentUser: state.auth.user.username || "",
  ncsId: state.app.ncsId,
  isLoggingIn: state.auth.isLoggingIn,
  appMode: state.app.appMode,
  configLoaded: state.app.configLoaded,
  runContext: state.app.runContext,
  assetLoading: state.asset.assetLoading || state.savedItems.savedItemLoading,
  isModifierActive: state.keyShortcuts.isModifierActive,
  inserting: state.asset.inserting,
  insertFailed: state.asset.insertFailed || false,
  insertedObjId: state.asset.insertedObjId,
  viewPortSize: state.app.viewPortSize,
  initializing: state.app.initializing,
  initialized: state.app.initialized,
  theme: state.userInterface.theme
});

const mapActionCreators = {
  openMosObject: assetActions.openMosObject,
  openPlaceholder: appActions.openPlaceholder,
  login: authActions.login,
  loadChannelList: assetActions.loadChannelList,
  setNcsId: appActions.setNcsId,
  setRunContext: appActions.setRunContext,
  loadConfig: appActions.loadConfig,
  loadUserConfig: appActions.loadUserConfig,
  setModifierKeyState: keyShortcutsActions.setModifierKeyState,
  showSnack: snackbarActions.showSnack,
  setViewPortSize: appActions.setViewPortSize,
  setAppRunMode: appActions.setAppRunMode,
  setContainerType: appActions.setContainerType,
  initialize: appActions.initialize,
  checkConnectionStatus: appActions.checkConnectionStatus,
  setDefaultContext: assetsActions.setDefaultContext,
  setNcsInfo: assetsActions.setNcsInfo
};

export default connect(mapStateToProps, mapActionCreators)(CoreLayout);



// WEBPACK FOOTER //
// ./app/layouts/CoreLayout.js